import React, { useState } from 'react';
import { Send, Plus, X, Loader2, CheckCircle, AlertCircle, Mail, FileText, Sparkles, Building2, Globe, Link2, Handshake, BadgeDollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { apiFetch } from '../lib/api';
import { NqCarePriceUpdatePreview } from './NqCarePriceUpdatePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_pet-jewellery-email/artifacts/uc95zhfe_%20Logo%20Pure%20Souls1200x975.png';
const NQCARE_LOGO_URL = '/pure-souls-logo-email-transparent.png';

// Product Images for Memorial Jewellery template
const IMAGES = {
  logo: LOGO_URL,
  bluePawHero: 'https://customer-assets.emergentagent.com/job_84ce8cf9-8fa7-494b-8267-d4501a90a336/artifacts/b2xc38fj_ChatGPT%20Image%20Jan%2010%2C%202026%20at%2005_14_26%20PM.PNG',
  pinkPaw: 'https://customer-assets.emergentagent.com/job_84ce8cf9-8fa7-494b-8267-d4501a90a336/artifacts/p4pepvdi_ChatGPT%20Image%20Jan%2010%2C%202026%20at%2005_32_11%20PM.PNG',
  bluePawWorn: 'https://customer-assets.emergentagent.com/job_84ce8cf9-8fa7-494b-8267-d4501a90a336/artifacts/ak215ft5_Blue%20Paw%20resin%20teardrop%20girl.PNG',
  pinkPawWorn: 'https://customer-assets.emergentagent.com/job_84ce8cf9-8fa7-494b-8267-d4501a90a336/artifacts/37s7osm1_ChatGPT%20Image%20Jan%2010%2C%202026%20at%2005_28_45%20PM.PNG',
  flowerDesign: 'https://customer-assets.emergentagent.com/job_pet-jewellery-email/artifacts/idi51rey_ChatGPT%20Image%20Jan%2010%2C%202026%20at%2005_43_05%20PM.PNG',
  heroDog: 'https://customer-assets.emergentagent.com/job_pet-jewellery-email/artifacts/2625xqqo_ChatGPT%20Image%20Jan%205%2C%202026%20at%2011_28_24%20AM.png',
  vetPortalRegister: 'https://customer-assets.emergentagent.com/job_2bb26788-4a49-4441-8890-cdc4309ef9dc/artifacts/addt3fld_Screenshot%202026-03-18%20at%204.20.37%E2%80%AFpm.png',
  vetPortalDashboard: 'https://customer-assets.emergentagent.com/job_2bb26788-4a49-4441-8890-cdc4309ef9dc/artifacts/d6evq1xp_IMG_1335.jpg',
  vetPortalClinics: 'https://customer-assets.emergentagent.com/job_2bb26788-4a49-4441-8890-cdc4309ef9dc/artifacts/1upyxctu_IMG_1352.jpg',
  nqCarePortalPrice: 'https://customer-assets.emergentagent.com/job_2984b506-f268-477b-9365-14a3a1d4c36b/artifacts/8rwrjcq6_Screenshot%202026-07-13%20at%209.49.26%E2%80%AFam.webp',
  nqCarePriceMenu: 'https://customer-assets.emergentagent.com/job_f03477a1-49f6-4081-a71c-47bbebb77b03/artifacts/6z4btbz3_Screenshot%202026-07-15%20at%2012.30.30%E2%80%AFpm.webp',
};

// Templates configuration
const TEMPLATES = {
  memorial_jewellery: {
    id: 'memorial_jewellery',
    name: 'Memorial Jewellery',
    description: 'Wholesale pendant promotion for water cremation operators',
    subject: 'A Unique Memorial Jewellery Piece Designed for Water Cremation 💧',
    icon: Sparkles,
  },
  clinic_outreach: {
    id: 'clinic_outreach',
    name: 'Clinic Outreach - Townsville',
    description: 'Service introduction for regional veterinary clinics',
    subject: 'Now offering Gentle Water Cremation Services to Regional Townsville Clinics',
    icon: Building2,
  },
  vet_portal: {
    id: 'vet_portal',
    name: 'Vet Portal Announcement',
    description: 'Announce the new online portal for partner clinics',
    subject: 'Exciting news from Pure Souls 🐾!',
    icon: Globe,
  },
  vet_portal_link: {
    id: 'vet_portal_link',
    name: 'Vet Portal Registration Link',
    description: 'Send the portal registration link to clinics',
    subject: 'Your Vet Portal is ready — Register now 🐾',
    icon: Link2,
  },
  partnership_invite: {
    id: 'partnership_invite',
    name: 'Partnership Invitation',
    description: 'Invite new clinics to join the partnership program',
    subject: 'Something new to make your day a little easier 🐾',
    icon: Handshake,
  },
  nqcare_price_update: {
    id: 'nqcare_price_update',
    name: 'NQ Care Price Update',
    description: 'Updated service pricing and Vet Portal reminder for NQ Care',
    subject: 'Updated Aquacremation Price List',
    icon: BadgeDollarSign,
  },
};

const EmailSender = () => {
  const [recipients, setRecipients] = useState(['']);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('send');
  const [selectedTemplate, setSelectedTemplate] = useState('memorial_jewellery');

  const addRecipient = () => setRecipients([...recipients, '']);
  const removeRecipient = (index) => {
    if (recipients.length > 1) setRecipients(recipients.filter((_, i) => i !== index));
  };
  const updateRecipient = (index, value) => {
    const updated = [...recipients];
    updated[index] = value;
    setRecipients(updated);
  };

  const handleSend = async () => {
    const validRecipients = recipients.filter(r => r.trim() && r.includes('@'));
    if (validRecipients.length === 0) {
      toast.error('Please add at least one valid recipient email');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await apiFetch(`${BACKEND_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipients: validRecipients,
          template: selectedTemplate,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ success: true, message: data.message, sent_to: data.sent_to, failed: data.failed, receipts: data.receipts || [] });
        toast.success(`SMTP accepted ${data.sent_to.length} email(s) for delivery`);
      } else {
        setResult({ success: false, message: data.detail || 'Failed to send email' });
        toast.error(data.detail || 'Failed to send email');
      }
    } catch (error) {
      setResult({ success: false, message: 'Network error. Please try again.' });
      toast.error('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="campaign-page">
      {/* Header */}
      <div className="bg-stone border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-7">
          <div className="flex items-center gap-3 mb-1.5">
            <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-primary flex-shrink-0" />
            <h1 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">Pure Souls Email Campaign</h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            Send beautifully branded emails to your families and partner clinics
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="send" className="flex items-center gap-2" data-testid="campaign-send-tab">
              <Send className="w-4 h-4" />
              Send Email
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2" data-testid="campaign-preview-tab">
              <FileText className="w-4 h-4" />
              Preview Template
            </TabsTrigger>
          </TabsList>

          {/* Send Tab */}
          <TabsContent value="send" className="mt-0">
            <div className="max-w-xl">
              
              {/* Template Selection */}
              <div className="bg-card rounded-lg border border-border p-6 mb-6">
                <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  Select Template
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.values(TEMPLATES).map((template) => {
                    const Icon = template.icon;
                    const isSelected = selectedTemplate === template.id;
                    return (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        data-testid={`campaign-template-${template.id}`}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-muted-foreground/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {template.name}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{template.description}</p>
                        {isSelected && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-primary">
                            <CheckCircle className="w-3 h-3" /> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Recipients */}
              <div className="bg-card rounded-lg border border-border p-6 mb-6">
                <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-muted-foreground" />
                  Recipients
                </h2>
                
                <div className="space-y-3">
                  {recipients.map((recipient, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        value={recipient}
                        onChange={(e) => updateRecipient(index, e.target.value)}
                        placeholder="recipient@example.com"
                        className="flex-1"
                        data-testid={`campaign-recipient-input-${index}`}
                      />
                      {recipients.length > 1 && (
                        <Button variant="outline" size="icon" onClick={() => removeRecipient(index)} data-testid={`campaign-remove-recipient-${index}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                <Button variant="outline" onClick={addRecipient} className="mt-4" data-testid="campaign-add-recipient-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Recipient
                </Button>
              </div>

              {/* Send Button */}
              <Button
                onClick={handleSend}
                disabled={sending}
                className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-accent rounded-full shadow-sm"
                data-testid="campaign-send-button"
              >
                {sending ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" />Send &ldquo;{TEMPLATES[selectedTemplate].name}&rdquo; Email</>
                )}
              </Button>

              {/* Result */}
              {result && (
                <div className={`mt-6 p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="campaign-send-result">
                  <div className="flex items-start gap-3">
                    {result.success ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />}
                    <div>
                      <p className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>{result.message}</p>
                      {result.sent_to?.length > 0 && (
                        <ul className="text-sm text-green-600 mt-2">
                          {result.sent_to.map((email, i) => <li key={i}>✓ {email}</li>)}
                        </ul>
                      )}
                      {result.receipts?.length > 0 && (
                        <div className="mt-3 space-y-2" data-testid="campaign-delivery-receipts">
                          {result.receipts.map(receipt => (
                            <div key={receipt.id} className="text-xs text-green-700" data-testid={`campaign-delivery-receipt-${receipt.id}`}>
                              <p>SMTP accepted {new Date(receipt.smtp_accepted_at).toLocaleString('en-AU')}</p>
                              <p className="break-all">{receipt.message_id}</p>
                              <p>{receipt.sent_folder_status === 'archived' ? 'Saved in Sent' : 'Sent-folder copy unavailable'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="mt-0">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="bg-muted px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  📧 Preview: <strong>{TEMPLATES[selectedTemplate].name}</strong>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {Object.values(TEMPLATES).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      data-testid={`campaign-preview-template-${template.id}`}
                      className={`px-3 py-1 text-xs rounded-full transition-all ${
                        selectedTemplate === template.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {selectedTemplate === 'memorial_jewellery' ? (
                  <MemorialJewelleryPreview />
                ) : selectedTemplate === 'clinic_outreach' ? (
                  <ClinicOutreachPreview />
                ) : selectedTemplate === 'vet_portal_link' ? (
                  <VetPortalLinkPreview />
                ) : selectedTemplate === 'vet_portal' ? (
                  <VetPortalPreview />
                ) : selectedTemplate === 'partnership_invite' ? (
                  <PartnershipInvitePreview />
                ) : selectedTemplate === 'nqcare_price_update' ? (
                  <NqCarePriceUpdatePreview logoUrl={NQCARE_LOGO_URL} priceMenuImageUrl={IMAGES.nqCarePriceMenu} portalImageUrl={IMAGES.nqCarePortalPrice} />
                ) : (
                  <MemorialJewelleryPreview />
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Memorial Jewellery Email Preview - FULL VERSION
const MemorialJewelleryPreview = () => (
  <div style={{
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: '#fcfaf8',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: '#3d3530',
    lineHeight: '1.7',
  }}>
    {/* Header */}
    <div style={{ padding: '32px 40px', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '80px', margin: '0 auto' }} />
    </div>

    {/* Hero */}
    <div style={{ padding: '32px 40px 48px', textAlign: 'center' }}>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: '32px', fontWeight: '400', margin: '0 0 40px 0', color: '#3d3530' }}>
        A Unique Memorial Jewellery Piece Designed for Water Cremation 💧
      </h1>
      <img src={IMAGES.bluePawHero} alt="Blue Paw" style={{ maxWidth: '350px', width: '100%' }} />
    </div>

    {/* Introduction */}
    <div style={{ padding: '48px 40px', backgroundColor: '#f9f6f3' }}>
      <div style={{ maxWidth: '580px', margin: '0 auto' }}>
        <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '18px', marginBottom: '24px', color: '#3d3530' }}>Dear Colleagues,</p>
        <p style={{ color: '#6b6560', marginBottom: '20px', fontSize: '16px' }}>
          We are reaching out from Pure Souls Pet Aqua Cremation to share a memorial jewellery piece that has become one of our most loved and requested offerings by families.
        </p>
        <p style={{ color: '#6b6560', marginBottom: '20px', fontSize: '16px' }}>
          This clear resin teardrop pendant was designed specifically for the water cremation space. Its shape and internal chamber allow it to safely house the pet&apos;s microchip once retrieved after the aquamation process. Many families find deep comfort in keeping the microchip close, as it represents identity, connection, and continuity beyond the farewell.
        </p>
        <p style={{ color: '#6b6560', fontSize: '16px' }}>
          Over time, we have also found that some families choose to place a small amount of ashes inside the pendant, or a combination of both ashes and microchip. The versatility of this piece has made it especially meaningful across a range of memorial journeys.
        </p>
      </div>
    </div>

    {/* Two Beautiful Designs */}
    <div style={{ padding: '48px 40px' }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '26px', textAlign: 'center', marginBottom: '16px', color: '#3d3530' }}>Two Beautiful Designs</h2>
      <p style={{ textAlign: 'center', color: '#6b6560', maxWidth: '500px', margin: '0 auto 40px', fontSize: '15px' }}>
        Each pendant features an intricate mandala-style paw print, available in soft blue or gentle pink.
      </p>
      <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', maxWidth: '220px' }}>
          <img src={IMAGES.bluePawHero} alt="Blue Paw" style={{ width: '100%', marginBottom: '16px' }} />
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: '18px', marginBottom: '8px', color: '#3d3530' }}>Blue Paw</h3>
          <p style={{ fontSize: '13px', color: '#6b6560' }}>A calming blue reminiscent of gentle waters</p>
        </div>
        <div style={{ textAlign: 'center', maxWidth: '220px' }}>
          <img src={IMAGES.pinkPaw} alt="Pink Paw" style={{ width: '100%', marginBottom: '16px' }} />
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: '18px', marginBottom: '8px', color: '#3d3530' }}>Pink Paw</h3>
          <p style={{ fontSize: '13px', color: '#6b6560' }}>A tender blush symbolising eternal love</p>
        </div>
      </div>
    </div>

    {/* Worn Close to the Heart */}
    <div style={{ padding: '48px 40px', backgroundColor: '#f3f0ed' }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '26px', textAlign: 'center', marginBottom: '16px', color: '#3d3530' }}>Worn Close to the Heart</h2>
      <p style={{ textAlign: 'center', color: '#6b6560', maxWidth: '520px', margin: '0 auto 40px', fontSize: '15px' }}>
        A delicate piece designed for everyday wear, allowing families to keep their beloved companion&apos;s memory close at all times.
      </p>
      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', width: '260px' }}>
          <img src={IMAGES.bluePawWorn} alt="Blue Paw worn" style={{ width: '260px', height: 'auto', borderRadius: '4px', display: 'block' }} />
        </div>
        <div style={{ textAlign: 'center', width: '260px' }}>
          <img src={IMAGES.pinkPawWorn} alt="Pink Paw worn" style={{ width: '260px', height: 'auto', borderRadius: '4px', display: 'block' }} />
        </div>
      </div>
      <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '14px', color: '#6b6560', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
        The pendant sits beautifully on a delicate chain, its glass-like clarity catching the light and drawing gentle admiration.
      </p>
    </div>

    {/* Flower Pendant */}
    <div style={{ padding: '48px 40px', backgroundColor: '#fdf9f8' }}>
      <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap', maxWidth: '600px', margin: '0 auto' }}>
        <img src={IMAGES.flowerDesign} alt="Flower Pendant" style={{ flex: '1', minWidth: '200px', maxWidth: '250px' }} />
        <div style={{ flex: '1', minWidth: '250px' }}>
          <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', color: '#8a8580', margin: '0 0 12px 0' }}>New Design</p>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: '24px', fontWeight: '400', margin: '0 0 16px 0', color: '#3d3530' }}>The Flower Pendant</h2>
          <p style={{ color: '#6b6560', margin: '0 0 16px 0', lineHeight: '1.7', fontSize: '15px' }}>
            A beautiful alternative featuring a delicate pink dahlia bloom suspended within the crystal-clear teardrop. This design offers a botanical tribute, representing growth, beauty, and the natural cycle of life.
          </p>
          <p style={{ color: '#6b6560', fontSize: '13px' }}>
            Available alongside the paw print designs, perfect for families seeking a non-traditional memorial option.
          </p>
        </div>
      </div>
    </div>

    {/* Product Details */}
    <div style={{ padding: '48px 40px' }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '26px', textAlign: 'center', marginBottom: '32px', color: '#3d3530' }}>Product Details</h2>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        {[
          { label: 'Material', value: 'High-quality clear resin with secure internal chamber' },
          { label: 'Shape', value: 'Teardrop / droplet form, symbolising water cremation' },
          { label: 'Charm', value: 'Silver angel wing charm included' },
          { label: 'Chain', value: 'Polished delicate stainless steel chain provided' },
          { label: 'Designs', value: 'Blue Paw, Pink Paw, and Flower available' },
          { label: 'Purpose', value: 'Houses microchip, ashes, or both' },
          { label: 'Size', value: 'Approximately 25mm pendant height' },
        ].map((item, idx) => (
          <div key={idx} style={{ display: 'flex', padding: '14px 0', borderBottom: '1px solid #e8e4e0' }}>
            <span style={{ fontWeight: '600', fontSize: '14px', color: '#3d3530', width: '120px', flexShrink: 0 }}>{item.label}</span>
            <span style={{ fontSize: '14px', color: '#6b6560' }}>{item.value}</span>
          </div>
        ))}
      </div>
      <p style={{ textAlign: 'center', fontSize: '13px', color: '#8a8580', marginTop: '32px' }}>
        Each pendant is carefully crafted and quality checked before delivery.
      </p>
    </div>

    {/* Why Families Love It */}
    <div style={{ padding: '48px 40px', backgroundColor: '#fdf9f8' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: '24px', fontWeight: '400', marginBottom: '24px', color: '#3d3530' }}>
          Why Families Love This Piece
        </h2>
        <p style={{ color: '#5a5550', margin: '0', lineHeight: '1.8', fontSize: '16px', fontStyle: 'italic' }}>
          &ldquo;It photographs beautifully, feels elegant and understated when worn, and aligns naturally with the values of water cremation — <strong style={{ color: '#3d3530' }}>gentleness, sustainability, and intimacy</strong>.&rdquo;
        </p>
      </div>
    </div>

    {/* Wholesale Partnership */}
    <div style={{ padding: '48px 40px', backgroundColor: '#f9f6f3' }}>
      <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', color: '#6ba3c7', margin: '0 0 12px 0', textAlign: 'center' }}>For Industry Partners</p>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '26px', marginBottom: '24px', color: '#3d3530', textAlign: 'center' }}>Wholesale Partnership</h2>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        <p style={{ color: '#5a5550', marginBottom: '20px', lineHeight: '1.7', fontSize: '15px' }}>
          We are pleased to offer these pendants to fellow water cremation professionals who share our commitment to compassionate aftercare.
        </p>
        <p style={{ color: '#5a5550', marginBottom: '24px', lineHeight: '1.7', fontSize: '15px' }}>
          At Pure Souls, we currently retail this pendant at <strong style={{ color: '#3d3530' }}>AUD $70</strong>, and families are very comfortable with this price point. This provides a <strong style={{ color: '#3d3530' }}>strong margin for operators</strong>, with the flexibility to maintain a similar retail price or position it at a higher value if desired.
        </p>
        
        {/* Wholesale Offer Box */}
        <div style={{ backgroundColor: '#ffffff', border: '2px solid #e8e4e0', borderRadius: '8px', padding: '28px', margin: '28px 0' }}>
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', marginBottom: '20px', color: '#3d3530', textAlign: 'center' }}>
            Wholesale Offer
          </h3>
          <div style={{ fontSize: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0ede9' }}>
              <span style={{ color: '#5a5550' }}><span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> Minimum order</span>
              <span style={{ color: '#3d3530', fontWeight: '600' }}>10 units per design</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0ede9' }}>
              <span style={{ color: '#5a5550' }}><span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> Wholesale price</span>
              <span style={{ color: '#3d3530', fontWeight: '600' }}>AUD $25 per pendant</span>
            </div>
            <div style={{ padding: '12px 0', color: '#5a5550' }}>
              <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> Chain, charm, and shipping within Australia <strong style={{ color: '#3d3530' }}>included</strong>
            </div>
          </div>
        </div>
        
        <p style={{ color: '#5a5550', marginBottom: '28px', lineHeight: '1.7', fontSize: '15px', textAlign: 'center' }}>
          We can also discuss custom branding or packaging to align with your business.
        </p>
        
        <div style={{ textAlign: 'center' }}>
          <a href="mailto:info@puresouls.com.au?subject=Wholesale%20Enquiry%20-%20Memorial%20Pendants" style={{ display: 'inline-block', padding: '16px 32px', backgroundColor: '#3d3530', color: '#fff', textDecoration: 'none', borderRadius: '3px', fontSize: '14px', fontWeight: '500', letterSpacing: '1px' }} data-testid="memorial-wholesale-enquiry-link">
            ENQUIRE ABOUT WHOLESALE
          </a>
        </div>
      </div>
    </div>

    {/* Closing */}
    <div style={{ padding: '48px 40px' }}>
      <div style={{ maxWidth: '540px', margin: '0 auto' }}>
        <p style={{ color: '#6b6560', marginBottom: '20px', lineHeight: '1.7', fontSize: '15px' }}>
          We have been offering this pendant to our families for some time now, and the response has been deeply touching. It has become a natural extension of the care we provide—a small but significant way to help families hold onto their connection with their pet.
        </p>
        <p style={{ color: '#6b6560', marginBottom: '32px', lineHeight: '1.7', fontSize: '15px' }}>
          If you believe this piece might resonate with your families, we would be delighted to share more details or arrange samples. We understand the importance of trust in this industry, and we stand behind the quality and sentiment of everything we offer.
        </p>
        <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '17px', textAlign: 'center', marginBottom: '8px', color: '#3d3530' }}>
          With warm regards,
        </p>
        <p style={{ textAlign: 'center', color: '#6b6560', fontSize: '15px' }}>
          The Team at Pure Souls Pet Aqua Cremation
        </p>
      </div>
    </div>

    {/* Footer */}
    <div style={{ padding: '36px 40px', backgroundColor: '#f3f0ed', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '50px', margin: '0 auto 20px' }} />
      <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
        <a href="mailto:info@puresouls.com.au" style={{ color: '#6b6560', textDecoration: 'none' }} data-testid="memorial-footer-email-link">info@puresouls.com.au</a>
      </p>
      <p style={{ fontSize: '12px', color: '#8a8580', marginBottom: '20px' }}>Australia</p>
      <p style={{ fontSize: '11px', color: '#a0a0a0', margin: '0' }}>
        © 2026 Pure Souls Pet Aqua Cremation. All rights reserved.<br/>
        Providing compassionate water cremation services for beloved pets.
      </p>
    </div>
  </div>
);

// Clinic Outreach Email Preview
const ClinicOutreachPreview = () => (
  <div style={{
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: '#fcfaf8',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: '#3d3530',
    lineHeight: '1.8',
  }}>
    <div style={{ padding: '32px 40px', textAlign: 'center', backgroundColor: '#ffffff' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '70px', margin: '0 auto' }} />
    </div>
    <div style={{ padding: '40px', textAlign: 'center', background: 'linear-gradient(180deg, #e8f4f8 0%, #fcfaf8 100%)' }}>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: '28px', fontWeight: '400', margin: '0 0 32px 0', color: '#3d3530' }}>
        Now offering Gentle Water Cremation Services to Regional Townsville Clinics
      </h1>
      <img src={IMAGES.heroDog} alt="Peaceful pet memorial" style={{ maxWidth: '400px', width: '100%', borderRadius: '8px' }} />
    </div>
    <div style={{ padding: '40px' }}>
      <p style={{ marginBottom: '24px' }}>Dear Team,</p>
      <p style={{ color: '#5a5550', marginBottom: '20px' }}>I hope this email finds you well.</p>
      <p style={{ color: '#5a5550', marginBottom: '20px' }}>
        I am writing to introduce <strong>Pure Souls Pet Aqua Cremation</strong> and to share that we are now extending our gentle water cremation services to regional veterinary clinics around Townsville.
      </p>
      <p style={{ color: '#5a5550', marginBottom: '20px' }}>
        Pure Souls has been established in Townsville for over a year now, during which time we have been proudly partnering with veterinary clinics across the region. We have received consistently positive feedback from both veterinary teams and the families we support.
      </p>
      <p style={{ color: '#5a5550' }}>
        We would love to invite your clinic to offer our services to your patients and their families. You are welcome to either reply directly to this email or complete our short <a href="https://forms.fillout.com/t/taTJsnvFY6us" style={{ color: '#6ba3c7', textDecoration: 'underline' }} data-testid="clinic-outreach-preference-form-link">aftercare preference form</a> to better understand your needs, and we will take care of the rest.
      </p>
    </div>
    <div style={{ padding: '40px', backgroundColor: '#f5f9fa' }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '22px', textAlign: 'center', marginBottom: '24px' }}>
        What We Offer Veterinary Clinics
      </h2>
      <div style={{ fontSize: '15px', color: '#5a5550' }}>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> <strong>Online Vet Portal</strong> to request pet collection easily
        </p>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> <strong>Same week collection</strong> using refrigerated vehicles
        </p>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> <strong>Streamlined client handling</strong> — we manage everything
        </p>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> <strong>Marketing materials</strong> and grieving support assets
        </p>
        <p style={{ padding: '10px 0' }}>
          <span style={{ color: '#6ba3c7', marginRight: '10px' }}>✦</span> <strong>10% staff discount</strong> for all clinic team members
        </p>
      </div>
    </div>
    <div style={{ padding: '40px' }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: '22px', textAlign: 'center', marginBottom: '24px' }}>
        What We Provide for Your Clients
      </h2>
      <div style={{ fontSize: '15px', color: '#5a5550' }}>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#d4a5a5', marginRight: '10px' }}>♡</span> Easy-to-use online booking portal
        </p>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#d4a5a5', marginRight: '10px' }}>♡</span> Flexible payment plans (VetPay, Afterpay, ZipPay)
        </p>
        <p style={{ padding: '10px 0', borderBottom: '1px solid #e8e4e0' }}>
          <span style={{ color: '#d4a5a5', marginRight: '10px' }}>♡</span> SMS updates and fast 5-day turnaround
        </p>
        <p style={{ padding: '10px 0' }}>
          <span style={{ color: '#d4a5a5', marginRight: '10px' }}>♡</span> Tree planting tribute for each pet
        </p>
      </div>
    </div>
    <div style={{ padding: '40px', backgroundColor: '#f9f6f3', textAlign: 'center' }}>
      <p style={{ color: '#5a5550', marginBottom: '24px' }}>
        We would be honoured to support your clinic and your clients.
      </p>
      <a href="#" style={{ display: 'inline-block', padding: '16px 32px', backgroundColor: '#3d3530', color: '#fff', textDecoration: 'none', borderRadius: '4px' }} data-testid="clinic-outreach-contact-link">
        GET IN TOUCH
      </a>
    </div>
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '17px', marginBottom: '8px' }}>Warm regards,</p>
      <p style={{ color: '#5a5550' }}>The Team at Pure Souls Pet Aqua Cremation</p>
    </div>
    <div style={{ padding: '32px 40px', backgroundColor: '#f3f0ed', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '50px', margin: '0 auto 16px' }} />
      <p style={{ fontSize: '14px', color: '#5a5550', marginBottom: '8px' }}>admin@puresouls.com.au</p>
      <p style={{ fontSize: '14px' }}><a href="#" style={{ color: '#6ba3c7', textDecoration: 'none' }} data-testid="clinic-outreach-website-link">puresouls.com.au</a></p>
      <p style={{ fontSize: '12px', color: '#8a8580', marginTop: '16px' }}>Townsville, Queensland, Australia</p>
    </div>
  </div>
);

// Vet Portal Announcement Email Preview
const VetPortalPreview = () => (
  <div style={{
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: '#3d3530',
    lineHeight: '1.8',
  }}>
    {/* Header */}
    <div style={{ padding: '32px 40px', textAlign: 'center', backgroundColor: '#ffffff' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '70px', margin: '0 auto' }} />
    </div>

    {/* Hero */}
    <div style={{ padding: '48px 40px', textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 50%, #fce4ec 100%)' }}>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#7cb342', margin: '0 0 16px 0', fontWeight: '500' }}>Exciting News</p>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: '32px', fontWeight: '400', lineHeight: '1.3', margin: '0', color: '#3d3530' }}>
        Something new is coming to make your day a little easier
      </h1>
      <p style={{ fontSize: '40px', margin: '20px 0 0 0' }}>✨</p>
    </div>

    {/* Greeting */}
    <div style={{ padding: '40px 40px 0 40px' }}>
      <p style={{ fontSize: '16px', marginBottom: '20px' }}>Dear Team,</p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>We hope you are having a lovely morning.</p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        Firstly, we would like to thank you for the <strong>care, compassion, and dedication</strong> you show to animals and their families every day. Supporting people through the loss of a beloved pet is never easy, and we truly respect the important role you play in those moments.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550' }}>
        At Pure Souls, we are always looking for ways to better support you and make our collaboration as smooth and helpful as possible. Over the past few months, we have been working on something we are very excited to share with you.
      </p>
    </div>

    {/* Announcement Box */}
    <div style={{ padding: '40px' }}>
      <div style={{ background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%)', borderRadius: '16px', padding: '32px', textAlign: 'center', border: '1px solid #c8e6c9' }}>
        <p style={{ fontSize: '14px', color: '#7cb342', margin: '0 0 12px 0', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>Introducing</p>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: '28px', fontWeight: '400', margin: '0 0 16px 0', color: '#3d3530' }}>
          Our New & Improved<br/>Vet Portal
        </h2>
        <p style={{ fontSize: '15px', color: '#5a5550', margin: '0', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
          Designed to keep your team informed, organised, and connected with Pure Souls in a simple and efficient way.
        </p>
      </div>
    </div>

    {/* Features */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', marginBottom: '24px', textAlign: 'center', color: '#3d3530' }}>
        With this new portal, you will be able to:
      </h3>
      {[
        { icon: '📍', color: '#81c784', title: 'Track real-time progress', desc: 'Monitor pets you refer while they are in our care' },
        { icon: '📋', color: '#64b5f6', title: 'Stay informed at each stage', desc: 'Follow their journey from collection to completion' },
        { icon: '⭐', color: '#ffb74d', title: 'View referral credits & rewards', desc: 'Monitor & redeem your points and rewards in one place' },
        { icon: '📁', color: '#f48fb1', title: 'Keep everything organised', desc: 'All your activity and information in one place' },
      ].map((feature, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', backgroundColor: '#fafafa', borderRadius: '12px', marginBottom: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0, backgroundColor: `${feature.color}30` }}>
            {feature.icon}
          </div>
          <div>
            <p style={{ margin: '0', fontSize: '15px', fontWeight: '500', color: '#3d3530' }}>{feature.title}</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#7a7570' }}>{feature.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Goal */}
    <div style={{ padding: '32px 40px', backgroundColor: '#f8faf9' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', margin: '0', textAlign: 'center', fontStyle: 'italic' }}>
        Our goal is to provide <strong>greater transparency</strong>, reduce the need for follow-ups, and give your team a reliable tool to stay updated <strong>without needing to call or email</strong>.
      </p>
    </div>

    {/* Getting Started */}
    <div style={{ padding: '40px' }}>
      <div style={{ backgroundColor: '#fff8e1', borderRadius: '12px', padding: '28px', textAlign: 'center', border: '1px solid #ffecb3' }}>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: '22px', fontWeight: '400', margin: '0 0 16px 0', color: '#3d3530' }}>
          Getting started is very simple
        </h3>
        <p style={{ fontSize: '15px', color: '#5a5550', margin: '0 0 8px 0' }}>
          We will be sending you a link where you can register in the next 5 minutes:
        </p>
        <p style={{ fontSize: '15px', color: '#5a5550', margin: '0' }}>
          <span style={{ color: '#f9a825' }}>①</span> Selecting your clinic name from a dropdown list<br/>
          <span style={{ color: '#f9a825' }}>②</span> Entering your email<br/>
          <span style={{ color: '#f9a825' }}>③</span> Creating your password
        </p>
        <p style={{ fontSize: '14px', color: '#7a7570', margin: '20px 0 0 0', fontStyle: 'italic' }}>
          Once registered, you will have immediate access to your Vet Portal.
        </p>
      </div>
    </div>

    {/* Portal Screenshots */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', marginBottom: '24px', textAlign: 'center', color: '#3d3530' }}>
        A Sneak Peek
      </h3>
      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <img src={IMAGES.vetPortalRegister} alt="Vet Portal Registration" style={{ width: '220px', height: 'auto', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
          <p style={{ fontSize: '13px', color: '#7a7570', margin: '10px 0 0 0' }}>Simple Registration</p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <img src={IMAGES.vetPortalDashboard} alt="Vet Portal Dashboard" style={{ width: '320px', height: 'auto', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
          <p style={{ fontSize: '13px', color: '#7a7570', margin: '10px 0 0 0' }}>Your Dashboard</p>
        </div>
      </div>
    </div>

    {/* Closing */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '28px', textAlign: 'center' }}>
        We are truly grateful for the trust you place in Pure Souls. We remain committed to supporting you, your team, and the families you care for by continually improving the way we work together.
      </p>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '18px', marginBottom: '8px', color: '#3d3530' }}>
          The Pure Souls Team
        </p>
        <p style={{ fontSize: '24px', margin: '0' }}>💕</p>
        <p style={{ fontSize: '14px', color: '#b0a8a0', marginTop: '8px', fontStyle: 'italic' }}>xoxo</p>
      </div>
    </div>

    {/* Footer */}
    <div style={{ padding: '32px 40px', backgroundColor: '#f3f0ed', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '50px', margin: '0 auto 16px' }} />
      <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
        <a href="mailto:admin@puresouls.com.au" style={{ color: '#5a5550', textDecoration: 'none' }} data-testid="vet-portal-footer-email-link">admin@puresouls.com.au</a>
      </p>
      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
        <a href="https://puresouls.com.au" style={{ color: '#7cb342', textDecoration: 'none' }} data-testid="vet-portal-footer-website-link">puresouls.com.au</a>
      </p>
      <p style={{ fontSize: '11px', color: '#a0a0a0', margin: '0' }}>
        © 2026 Pure Souls Pet Aqua Cremation. All rights reserved.
      </p>
    </div>
  </div>
);

// Vet Portal Registration Link Email Preview
const VetPortalLinkPreview = () => (
  <div style={{
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: '#3d3530',
    lineHeight: '1.8',
  }}>
    {/* Header */}
    <div style={{ padding: '32px 40px', textAlign: 'center', backgroundColor: '#ffffff' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '70px', margin: '0 auto' }} />
    </div>

    {/* Hero */}
    <div style={{ padding: '40px 40px 32px', textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 50%, #f1f8e9 100%)' }}>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#7cb342', margin: '0 0 16px 0', fontWeight: '500' }}>As Promised</p>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: '30px', fontWeight: '400', lineHeight: '1.3', margin: '0', color: '#3d3530' }}>
        Your Vet Portal is ready
      </h1>
      <p style={{ fontSize: '16px', color: '#5a5550', margin: '16px 0 0 0' }}>
        Register now and get instant access
      </p>
      <p style={{ fontSize: '32px', margin: '16px 0 0 0' }}>🐾</p>
    </div>

    {/* Greeting */}
    <div style={{ padding: '40px 40px 0 40px' }}>
      <p style={{ fontSize: '16px', marginBottom: '20px' }}>Dear Team,</p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        As mentioned in our previous email, here is your link to register for the <strong>Pure Souls Vet Portal</strong>.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550' }}>
        The process takes less than a minute and you will have immediate access once completed. Once you are logged in, we recommend bookmarking the portal page for quick and easy access in the future.
      </p>
    </div>

    {/* Register CTA */}
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <a href="https://vet-portal.puresouls.com.au/register" style={{ display: 'inline-block', padding: '18px 48px', backgroundColor: '#26a69a', color: '#fff', fontSize: '16px', fontWeight: '600', textDecoration: 'none', borderRadius: '8px', letterSpacing: '0.5px' }} data-testid="vet-portal-registration-button-link">
        REGISTER YOUR CLINIC
      </a>
      <p style={{ fontSize: '13px', color: '#7a7570', margin: '16px 0 0 0' }}>
        or copy this link: <a href="https://vet-portal.puresouls.com.au/register" style={{ color: '#26a69a', textDecoration: 'underline' }} data-testid="vet-portal-registration-text-link">vet-portal.puresouls.com.au/register</a>
      </p>
    </div>

    {/* Steps */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <div style={{ backgroundColor: '#f8faf9', borderRadius: '12px', padding: '28px', border: '1px solid #e8f5e9' }}>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', margin: '0 0 20px 0', textAlign: 'center', color: '#3d3530' }}>
          Three simple steps
        </h3>
        {[
          { num: '1', color: '#43a047', bg: '#e8f5e9', title: 'Select your clinic name', desc: 'Find your clinic from the alphabetical dropdown list' },
          { num: '2', color: '#1e88e5', bg: '#e3f2fd', title: 'Enter your email', desc: 'Use your clinic email address' },
          { num: '3', color: '#e91e63', bg: '#fce4ec', title: 'Create your password', desc: 'Minimum 6 characters and you are in!' },
        ].map((step, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: '#ffffff', borderRadius: '8px', marginBottom: idx < 2 ? '8px' : '0' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: step.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '600', color: step.color, flexShrink: 0 }}>
              {step.num}
            </div>
            <div>
              <p style={{ margin: '0', fontSize: '15px', fontWeight: '500', color: '#3d3530' }}>{step.title}</p>
              <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#7a7570' }}>{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Screenshot */}
    <div style={{ padding: '0 40px 40px 40px', textAlign: 'center' }}>
      <p style={{ fontSize: '14px', color: '#7a7570', margin: '0 0 16px 0', fontStyle: 'italic' }}>Here is what the registration page looks like:</p>
      <img src={IMAGES.vetPortalClinics} alt="Registration - Select your clinic" style={{ width: '380px', height: 'auto', borderRadius: '12px', border: '1px solid #e0e0e0' }} />
    </div>

    {/* Help */}
    <div style={{ padding: '32px 40px', backgroundColor: '#fff8e1', textAlign: 'center' }}>
      <p style={{ fontSize: '15px', color: '#5a5550', margin: '0' }}>
        Can&apos;t find your clinic in the list? No worries, simply reply to this email and we will add you straight away.
      </p>
    </div>

    {/* Closing */}
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '28px' }}>
        Thank you for being part of the Pure Souls family. We look forward to working together through the portal.
      </p>
      <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '18px', marginBottom: '8px', color: '#3d3530' }}>
        The Pure Souls Team
      </p>
      <p style={{ fontSize: '24px', margin: '0' }}>💕</p>
    </div>

    {/* Footer */}
    <div style={{ padding: '32px 40px', backgroundColor: '#f3f0ed', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '50px', margin: '0 auto 16px' }} />
      <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
        <a href="mailto:admin@puresouls.com.au" style={{ color: '#5a5550', textDecoration: 'none' }} data-testid="vet-portal-link-footer-email-link">admin@puresouls.com.au</a>
      </p>
      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
        <a href="https://puresouls.com.au" style={{ color: '#7cb342', textDecoration: 'none' }} data-testid="vet-portal-link-footer-website-link">puresouls.com.au</a>
      </p>
      <p style={{ fontSize: '11px', color: '#a0a0a0', margin: '0' }}>
        © 2026 Pure Souls Pet Aqua Cremation. All rights reserved.
      </p>
    </div>
  </div>
);

// Partnership Invitation Email Preview
const PartnershipInvitePreview = () => (
  <div style={{
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: '#3d3530',
    lineHeight: '1.8',
  }}>
    {/* Header */}
    <div style={{ padding: '32px 40px', textAlign: 'center', backgroundColor: '#ffffff' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '70px', margin: '0 auto' }} />
    </div>

    {/* Hero */}
    <div style={{ padding: '48px 40px', textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 50%, #fce4ec 100%)' }}>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#7cb342', margin: '0 0 16px 0', fontWeight: '500' }}>You Are Invited</p>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: '30px', fontWeight: '400', lineHeight: '1.3', margin: '0', color: '#3d3530' }}>
        Something new is coming to make your day a little easier
      </h1>
      <p style={{ fontSize: '40px', margin: '20px 0 0 0' }}>✨</p>
    </div>

    {/* Greeting */}
    <div style={{ padding: '40px 40px 0 40px' }}>
      <p style={{ fontSize: '16px', marginBottom: '20px' }}>Dear Team,</p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>We hope you are having a lovely morning.</p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        Firstly, we would like to acknowledge the <strong>care, compassion, and dedication</strong> you show to animals and their families every day. Supporting people through the loss of a beloved pet is never easy, and we truly respect the important role you play in those moments.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        At Pure Souls, we are always looking for ways to support veterinary clinics and make this process as smooth and reassuring as possible for both your team and your clients.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        Over the past few months, we have been working closely with local clinics to improve how we collaborate, and we are now inviting you to be part of our partnership program.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550' }}>
        <strong>There are no upfront costs, and getting started is simple.</strong>
      </p>
    </div>

    {/* Benefits Header */}
    <div style={{ padding: '40px' }}>
      <div style={{ background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%)', borderRadius: '16px', padding: '32px', textAlign: 'center', border: '1px solid #c8e6c9' }}>
        <p style={{ fontSize: '14px', color: '#7cb342', margin: '0 0 12px 0', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>Through Our Vet Portal</p>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: '24px', fontWeight: '400', margin: '0', color: '#3d3530' }}>
          Your clinic will have access to
        </h2>
      </div>
    </div>

    {/* Benefits */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      {[
        { icon: '✅', color: '#81c784', title: 'No upfront costs', desc: 'Start right away with no fees or commitments' },
        { icon: '⭐', color: '#ffb74d', title: 'Rewarding system', desc: 'Earn credits for each successful client referral and collection lodgement' },
        { icon: '🎁', color: '#f48fb1', title: 'Added value for your clients', desc: 'Offer your clients special benefits, including exclusive offers and coupons' },
        { icon: '⚡', color: '#64b5f6', title: 'Simple and convenient process', desc: 'A streamlined online system designed to save your team time' },
        { icon: '📦', color: '#ce93d8', title: 'Promotional assets and consumables', desc: 'Access to brochures, tags, and supporting materials for your clinic' },
        { icon: '💙', color: '#4dd0e1', title: 'Team benefits', desc: 'Enjoy a 10% discount for your staff on all our products and services' },
      ].map((feature, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', backgroundColor: '#fafafa', borderRadius: '12px', marginBottom: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0, backgroundColor: `${feature.color}30` }}>
            {feature.icon}
          </div>
          <div>
            <p style={{ margin: '0', fontSize: '15px', fontWeight: '500', color: '#3d3530' }}>{feature.title}</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#7a7570' }}>{feature.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Social Proof */}
    <div style={{ padding: '32px 40px', backgroundColor: '#f8faf9' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', margin: '0', textAlign: 'center', fontStyle: 'italic' }}>
        Clinics we are already working with have found the system <strong>easy to use</strong> and helpful in keeping everything <strong>organised and transparent</strong>.
      </p>
    </div>

    {/* Vet Portal Features */}
    <div style={{ padding: '40px' }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', marginBottom: '24px', textAlign: 'center', color: '#3d3530' }}>
        With the Vet Portal, you will be able to:
      </h3>
      {[
        { icon: '📍', color: '#81c784', title: 'Track real-time progress', desc: 'Monitor pets you refer while they are in our care' },
        { icon: '📋', color: '#64b5f6', title: 'Stay informed at each stage', desc: 'Follow their journey from collection to completion' },
        { icon: '⭐', color: '#ffb74d', title: 'View referral credits & rewards', desc: 'Monitor & redeem your points and rewards in one place' },
        { icon: '📁', color: '#f48fb1', title: 'Keep everything organised', desc: 'All your activity and information in one place' },
      ].map((feature, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', backgroundColor: '#fafafa', borderRadius: '12px', marginBottom: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0, backgroundColor: `${feature.color}30` }}>
            {feature.icon}
          </div>
          <div>
            <p style={{ margin: '0', fontSize: '15px', fontWeight: '500', color: '#3d3530' }}>{feature.title}</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#7a7570' }}>{feature.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Sneak Peek */}
    <div style={{ padding: '40px' }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', marginBottom: '24px', textAlign: 'center', color: '#3d3530' }}>
        A Sneak Peek of the Vet Portal
      </h3>
      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <img src={IMAGES.vetPortalRegister} alt="Vet Portal Registration" style={{ width: '220px', height: 'auto', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
          <p style={{ fontSize: '13px', color: '#7a7570', margin: '10px 0 0 0' }}>Simple Registration</p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <img src={IMAGES.vetPortalDashboard} alt="Vet Portal Dashboard" style={{ width: '320px', height: 'auto', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
          <p style={{ fontSize: '13px', color: '#7a7570', margin: '10px 0 0 0' }}>Your Dashboard</p>
        </div>
      </div>
    </div>

    {/* Intention */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '20px' }}>
        Our intention is to support you with a reliable, compassionate service while also providing tools that make your day-to-day workflow easier.
      </p>
      <p style={{ fontSize: '16px', color: '#5a5550' }}>
        <strong>We would truly value the opportunity to work alongside your team.</strong>
      </p>
    </div>

    {/* Registration Info */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <div style={{ backgroundColor: '#fff8e1', borderRadius: '12px', padding: '28px', textAlign: 'center', border: '1px solid #ffecb3' }}>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: '20px', fontWeight: '400', margin: '0 0 16px 0', color: '#3d3530' }}>
          Getting started is very simple
        </h3>
        <p style={{ fontSize: '15px', color: '#5a5550', margin: '0 0 8px 0' }}>
          We will be sending you a link to register in the next 5 minutes, where you can simply:
        </p>
        <p style={{ fontSize: '15px', color: '#5a5550', margin: '0' }}>
          <span style={{ color: '#f9a825' }}>①</span> Select your clinic name from a dropdown list<br/>
          <span style={{ color: '#f9a825' }}>②</span> Enter your email<br/>
          <span style={{ color: '#f9a825' }}>③</span> Create your password and gain access to the Vet Portal
        </p>
      </div>
    </div>

    {/* Help */}
    <div style={{ padding: '0 40px 40px 40px' }}>
      <p style={{ fontSize: '15px', color: '#5a5550', margin: '0', textAlign: 'center' }}>
        If you have any questions or would like to speak with us directly, we are always here to help.
      </p>
    </div>

    {/* Closing */}
    <div style={{ padding: '0 40px 40px 40px', textAlign: 'center' }}>
      <p style={{ fontSize: '16px', color: '#5a5550', marginBottom: '28px' }}>
        Thank you again for the work you do each day. We look forward to connecting with you.
      </p>
      <p style={{ fontFamily: "Georgia, serif", fontStyle: 'italic', fontSize: '18px', marginBottom: '8px', color: '#3d3530' }}>
        The Pure Souls Team
      </p>
      <p style={{ fontSize: '24px', margin: '0' }}>💕</p>
      <p style={{ fontSize: '14px', color: '#b0a8a0', marginTop: '8px', fontStyle: 'italic' }}>xoxo</p>
    </div>

    {/* Footer */}
    <div style={{ padding: '32px 40px', backgroundColor: '#f3f0ed', textAlign: 'center' }}>
      <img src={IMAGES.logo} alt="Pure Souls" style={{ height: '50px', margin: '0 auto 16px' }} />
      <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
        <a href="mailto:admin@puresouls.com.au" style={{ color: '#5a5550', textDecoration: 'none' }} data-testid="partnership-footer-email-link">admin@puresouls.com.au</a>
      </p>
      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
        <a href="https://puresouls.com.au" style={{ color: '#7cb342', textDecoration: 'none' }} data-testid="partnership-footer-website-link">puresouls.com.au</a>
      </p>
      <p style={{ fontSize: '11px', color: '#a0a0a0', margin: '0' }}>
        © 2026 Pure Souls Pet Aqua Cremation. All rights reserved.
      </p>
    </div>
  </div>
);

export default EmailSender;
